<template>
	<div class="user-input-form-renderer">
		<div :class="['form-fields', `layout-${schema.layout || 'vertical'}`]">
			<div v-for="field in visibleFields" :key="field.key" :class="['field-wrapper', `width-${field.width || 'full'}`]">
				<!-- Text Field. LSP-16465: :counter + maxLength rule warn on over-limit (counter goes red); do NOT add maxlength/:maxlength — fields never hard-cap input.
				     LSP-16498: PDF header overflow is handled by truncating the title in buildPdfHeaderPayload, not by capping input here. -->

				<v-text-field
					v-if="field.type === 'text' || field.type === 'phone' || field.type === 'url'"
					v-model="formValues[field.key]"
					@update:model-value="handleFieldChange(field)"
					:placeholder="field.placeholder"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:rules="getValidationRules(field)"
					:counter="field.validation?.maxLength"
					:hide-details="field.validation?.maxLength != null ? false : 'auto'"
					:type="getInputType(field.type)"
					:disabled="disabled || isFieldDisabled(field)"
					variant="outlined"
					density="compact">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</v-text-field>

				<EmailTextField
					v-else-if="field.type === 'email'"
					v-model="formValues[field.key]"
					:placeholder="field.placeholder"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:rules="getValidationRules(field)"
					:counter="field.validation?.maxLength"
					:disabled="disabled || isFieldDisabled(field)"
					invalid-message="Invalid email format"
					variant="outlined"
					density="compact"
					@update:model-value="handleFieldChange(field)">
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</EmailTextField>

				<!-- Number Field -->
				<!-- Use type="text" (not "number") so non-numeric characters such as 'e', '+', '-' are
				     preserved in .value and caught by validation rules, rather than silently discarded
				     by the browser's type="number" constraint. With type="number" + v-model.number,
				     typing 'e' leaves the Vue model empty while the browser still displays the character,
				     causing Vuetify to keep the floating label in-field and overlap the typed text.
				     Mirrors the pattern in WebFormV2.getInputType(). (guards LSP-16496) -->
				<v-text-field
					v-else-if="field.type === 'number'"
					v-model="formValues[field.key]"
					@update:model-value="handleFieldChange(field)"
					:placeholder="field.placeholder"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:rules="getValidationRules(field)"
					type="text"
					:disabled="disabled || isFieldDisabled(field)"
					variant="outlined"
					density="compact">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</v-text-field>

				<!-- Textarea -->
				<v-textarea
					v-else-if="field.type === 'textarea'"
					v-model="formValues[field.key]"
					@update:model-value="handleFieldChange(field)"
					:placeholder="field.placeholder"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:rules="getValidationRules(field)"
					:disabled="disabled || isFieldDisabled(field)"
					variant="outlined"
					density="compact"
					rows="3"
					auto-grow>
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</v-textarea>

				<!-- Select (Single) -->
				<v-select
					v-else-if="field.type === 'select'"
					v-model="formValues[field.key]"
					:placeholder="field.placeholder"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:items="getFieldOptions(field)"
					item-title="label"
					item-value="value"
					:rules="getValidationRules(field)"
					:loading="loadingFields[field.key]"
					:disabled="disabled || isFieldDisabled(field)"
					variant="outlined"
					density="compact"
					:no-data-text="getNoDataText(field)"
					@update:model-value="handleFieldChange(field)">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</v-select>

				<!-- Multi-Select -->
				<v-select
					v-else-if="field.type === 'multiselect'"
					v-model="formValues[field.key]"
					:placeholder="field.placeholder"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:items="getFieldOptions(field)"
					item-title="label"
					item-value="value"
					:rules="getValidationRules(field)"
					:loading="loadingFields[field.key]"
					:disabled="disabled || isFieldDisabled(field)"
					variant="outlined"
					density="compact"
					multiple
					chips
					closable-chips
					:no-data-text="getNoDataText(field)"
					@update:model-value="handleFieldChange(field)">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</v-select>

				<!-- Autocomplete (MCP Search) -->
				<!-- DO NOT ADD `auto-select-first` to the v-autocomplete below without revisiting the blur
				     commit. Two separate reviews have now reported that picking a result destroys a committed
				     value, and both depend on that prop being ON. Vuetify 3.12.6 (VAutocomplete.js) gates BOTH
				     focus-moving paths on it: `e.key === 'ArrowDown' && highlightFirst.value` calls
				     `listRef.value?.focus('next')`, and the auto-select runs only under
				     `highlightFirst.value && ['Enter','Tab']`. With the prop off, ArrowDown keeps DOM focus in
				     the input, so no blur fires and no commit runs mid-navigation. A MOUSE pick is safe for a
				     different reason entirely: the results `VList` carries `onMousedown: e => e.preventDefault()`.
				     So turning this on would open the keyboard door while the mouse door stays shut. If it is ever
				     wanted, teach the blur commit to stand down while a pick is landing first: as written it takes
				     the synchronous NoPendingLookup exit with the PRE-pick partial query, and reportUnresolvedEntry
				     then nulls the value and empties mcpOptions out from under the pick. -->
				<v-autocomplete
					v-else-if="field.type === 'autocomplete' || field.type === 'multi_autocomplete'"
					v-model="formValues[field.key]"
					:placeholder="field.placeholder || 'Type to search...'"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:items="getFieldOptions(field)"
					item-title="label"
					item-value="value"
					:rules="getValidationRules(field)"
					:loading="loadingFields[field.key]"
					:disabled="disabled || isFieldDisabled(field)"
					variant="outlined"
					density="compact"
					:multiple="field.type === 'multi_autocomplete'"
					:chips="field.type === 'multi_autocomplete'"
					:closable-chips="field.type === 'multi_autocomplete'"
					:no-data-text="getNoDataText(field)"
					:search="autocompleteSearchText[field.key]"
					:error-messages="rejectedLookupMessages(field)"
					:no-filter="true"
					:menu-props="fieldErrors[field.key] ? { modelValue: false } : {}"
					@update:search="(val: string | null) => handleAutocompleteSearch(field, val)"
					@update:model-value="handleFieldChange(field)"
					@blur="trackAutocompleteCommit(commitTypedAutocompleteValue(field, 'blur'))"
					@keydown.enter="trackAutocompleteCommit(commitTypedAutocompleteValue(field, 'enter'))"
					clearable
					:clear-on-select="field.type !== 'multi_autocomplete'">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
					<!-- This slot REPLACES :no-data-text, so the threshold instruction has to be rendered
						 here to be seen at all — getNoDataText() alone never reaches the user for an
						 autocomplete field. Without the middle branch a 6-character job number reads as a
						 failed search for its first five keystrokes. -->
					<template #no-data>
						<v-list-item v-if="!fieldErrors[field.key]">
							<v-list-item-title v-if="loadingFields[field.key]"> Searching... </v-list-item-title>
							<v-list-item-title v-else-if="!autocompleteSearchText[field.key]"> Type to search </v-list-item-title>
							<v-list-item-title v-else-if="autocompleteSearchText[field.key].length < getMinSearchChars(field)">
								Type at least {{ getMinSearchChars(field) }} characters to search
							</v-list-item-title>
							<v-list-item-title v-else> No results found </v-list-item-title>
						</v-list-item>
					</template>
				</v-autocomplete>

				<!-- Radio Group -->
				<v-radio-group
					v-else-if="field.type === 'radio'"
					v-model="formValues[field.key]"
					@update:model-value="handleFieldChange(field)"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:rules="getValidationRules(field)"
					:disabled="disabled || isFieldDisabled(field)"
					density="compact">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
					<v-radio
						v-for="option in getFieldOptions(field)"
						:key="String(option.value)"
						:label="option.label"
						:value="option.value" />
				</v-radio-group>

				<!-- Checkbox -->
				<v-checkbox
					v-else-if="field.type === 'checkbox'"
					v-model="formValues[field.key]"
					@update:model-value="handleFieldChange(field)"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:rules="getValidationRules(field)"
					:disabled="disabled || isFieldDisabled(field)"
					density="compact">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</v-checkbox>

				<!-- Date -->
				<v-text-field
					v-else-if="field.type === 'date'"
					v-model="formValues[field.key]"
					@update:model-value="handleFieldChange(field)"
					:placeholder="field.placeholder"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:rules="getValidationRules(field)"
					type="date"
					:disabled="disabled || isFieldDisabled(field)"
					variant="outlined"
					density="compact">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</v-text-field>

				<!-- DateTime -->
				<v-text-field
					v-else-if="field.type === 'datetime'"
					v-model="formValues[field.key]"
					@update:model-value="handleFieldChange(field)"
					:placeholder="field.placeholder"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:rules="getValidationRules(field)"
					type="datetime-local"
					:disabled="disabled || isFieldDisabled(field)"
					variant="outlined"
					density="compact">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</v-text-field>

				<!-- Time -->
				<v-text-field
					v-else-if="field.type === 'time'"
					v-model="formValues[field.key]"
					@update:model-value="handleFieldChange(field)"
					:placeholder="field.placeholder"
					:hint="field.description"
					:persistent-hint="!!field.description"
					:rules="getValidationRules(field)"
					type="time"
					:disabled="disabled || isFieldDisabled(field)"
					variant="outlined"
					density="compact">
					<!-- v-if is load-bearing, not defensive noise: with the old `:label` prop Vuetify
						 computed `hasLabel = !!(props.label || slots.label)`, so an empty label rendered
						 NO label element. A slot is always truthy regardless of what it renders, so an
						 unconditional #label makes Vuetify emit an empty `.v-label` for any field whose
						 label is blank — a visible indent/padding shift versus before. getRequiredLabel()
						 returns `field.label` untouched for non-required fields, so a schema-driven field
						 with no label reaches this. Mirrors BaseTextField/BaseSelect/IndustryPicker. -->
					<template v-if="getRequiredLabel(field)" #label><FieldLabel :label="getRequiredLabel(field)" /></template>
				</v-text-field>
			</div>
		</div>

		<!-- Entries the user typed that could not be resolved. Separate from the fetch-error alert below:
			 different cause, different copy, and this one must NOT suppress the dropdown, since the message
			 tells the user to pick from it. -->
		<v-alert v-if="Object.keys(unresolvedEntriesForBanner).length > 0" type="warning" variant="tonal" density="compact" class="mt-2">
			<div class="text-body-2">Please check these entries:</div>
			<ul class="text-caption mt-1 mb-0">
				<li v-for="(message, fieldKey) in unresolvedEntriesForBanner" :key="fieldKey">
					{{ getFieldLabelByKey(String(fieldKey)) }}: {{ message }}
				</li>
			</ul>
		</v-alert>

		<!-- Error display for MCP fetch errors -->
		<v-alert v-if="Object.keys(unavailableLookupErrors).length > 0" type="warning" variant="tonal" density="compact" class="mt-2">
			<div class="text-body-2">Some options could not be loaded:</div>
			<ul class="text-caption mt-1 mb-0">
				<li v-for="(failure, fieldKey) in unavailableLookupErrors" :key="fieldKey">
					{{ getFieldLabelByKey(String(fieldKey)) }}: {{ failure.message }}
				</li>
			</ul>
		</v-alert>
	</div>
</template>

<script lang="ts" setup>
	import { useAutocompleteCommitBarrier } from "./autocompleteCommitBarrier";
	import { ref, computed, watch, onMounted, onBeforeUnmount, onUnmounted } from "vue";
	import { fetchUserInputOptions, type UserInputFieldOption } from "../api";
	import { getCustomerDisplayName } from "@/formatters/customers";
	import { isNumericFieldValueValid } from "@/helpers/Validation";
	import { isUserInputFieldVisible } from "../utils/fieldVisibility";
	import type { Customer } from "../types";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	interface FieldOption {
		label: string;
		value: string | number | boolean;
	}

	interface McpDataSource {
		toolName: string;
		platformCodeName: string;
		labelTemplate?: string;
		valueField?: string;
		searchParam?: string;
		dependsOn?: string[];
		inputParams?: Record<string, unknown>;
		inputParamMappings?: Array<{
			paramName: string;
			sourceField: string;
			sourceValuePath: string;
		}>;
		outputFields?: Array<{ name: string; type: string; description: string }>;
		/**
		 * How many characters to require before searching, when that differs from the field's
		 * `validation.minLength`. The escape hatch for a field whose committed value is long but whose
		 * search is usefully narrower — declared per field rather than imposed as a global ceiling.
		 */
		minSearchChars?: number;
	}

	interface FieldDefinition {
		key: string;
		label: string;
		type: string;
		placeholder?: string;
		description?: string;
		defaultValue?: any;
		options?: FieldOption[];
		hidden?: boolean;
		width?: string;
		validation?: {
			required?: boolean;
			minLength?: number;
			maxLength?: number;
			min?: number;
			max?: number;
			pattern?: string;
			patternMessage?: string;
		};
		visibleWhen?: {
			field: string;
			operator: string;
			value: any;
		};
		mcpDataSource?: McpDataSource;
	}

	interface FormSchema {
		fields: FieldDefinition[];
		layout?: "vertical" | "horizontal";
	}

	type McpOptionWithProvenance = UserInputFieldOption & {
		record?: Record<string, unknown>;
		_mcpRequestParams?: Record<string, unknown>;
	};

	const props = withDefaults(defineProps<{
		schema: FormSchema;
		requirementKey: string;
		modelValue: Record<string, any>;
		disabled?: boolean;
		validationEnabled?: boolean;
		customer?: Customer | null;
	}>(), {
		validationEnabled: true
	});

	const emit = defineEmits<{
		"update:modelValue": [value: Record<string, any>];
	}>();

	const formValues = ref<Record<string, any>>({});
	const mcpOptions = ref<Record<string, UserInputFieldOption[]>>({});
	const loadingFields = ref<Record<string, boolean>>({});
	const fieldRequestGenerations = ref<Record<string, number>>({});
	/**
	 * Why a field's options could not be fetched — and, the part that decides WHERE it renders, whether that
	 * is the user's problem or the backend's (LSP-19462). `rejected-input` is a search the tool refused; it
	 * renders under the field as the field's own error, worded from the field's validation config.
	 * `unavailable` is everything else (timeout, 5xx, network) and keeps the "could not be loaded" banner,
	 * because nothing the user types will fix it.
	 */
	type LookupFailure = { kind: "rejected-input" | "unavailable"; message: string };
	/**
	 * What a failed fetch was, for the commit path. `below-minimum` never reaches fieldErrors: it is the
	 * backend saying the query is too short to search yet, which is the gate's job, not an error (LSP-18801).
	 */
	type FetchFailureKind = LookupFailure["kind"] | "below-minimum";
	const fieldErrors = ref<Record<string, LookupFailure>>({});
	/**
	 * Entries the user typed that could not be resolved. Deliberately NOT fieldErrors: that channel means
	 * "options could not be loaded" (its alert says so) and it is wired to `:menu-props` to SUPPRESS the
	 * dropdown — so reusing it rendered these messages under the wrong heading and hid the very list the
	 * message tells the user to pick from.
	 */
	const unresolvedEntries = ref<Record<string, string>>({});
	/** The failures the banner still owns: anything the user cannot fix by retyping (LSP-19462). */
	const unavailableLookupErrors = computed<Record<string, LookupFailure>>(() =>
		Object.fromEntries(Object.entries(fieldErrors.value).filter(([, failure]) => failure.kind === "unavailable"))
	);
	/**
	 * A rejected entry's explanation sits under its field, so the banner skips that field — one message per
	 * field at a time (LSP-19462). The ENTRY is still recorded, so proceed stays blocked on it.
	 */
	const unresolvedEntriesForBanner = computed<Record<string, string>>(() =>
		Object.fromEntries(
			Object.entries(unresolvedEntries.value).filter(([fieldKey]) => fieldErrors.value[fieldKey]?.kind !== "rejected-input")
		)
	);
	/** What the autocomplete shows as its own error: the rejection of what was typed into it, if any. */
	const rejectedLookupMessages = (field: FieldDefinition): string[] => {
		const failure = fieldErrors.value[field.key];
		return failure?.kind === "rejected-input" ? [failure.message] : [];
	};

	// The HOSTING DIALOG's barrier, so every renderer in its v-for tracks into the same one and its
	// proceed waits for all of them. Standalone mounts get a private barrier and a warning.
	const commitBarrier = useAutocompleteCommitBarrier();
	// Remembered so this renderer can withdraw its OWN work if it goes away — a step change unmounts
	// it while its siblings and the dialog live on, so clear() would be far too broad.
	const ownCommits = new Set<Promise<unknown>>();
	const trackAutocompleteCommit = (commit: Promise<unknown>) => {
		ownCommits.add(commit);
		void commit.catch(() => undefined).finally(() => ownCommits.delete(commit));
		commitBarrier.track(commit);
	};
	const autocompleteSearchText = ref<Record<string, string>>({});

	// Counts USER-originated changes to an autocomplete field: typing or pasting a non-empty query, or
	// clearing the field. Vuetify's own post-blur `update:search("")` deliberately does NOT count — the
	// blur-commit path must tolerate that one, and a counter is the only way to tell it apart from the
	// user deliberately clearing the input afterwards, which must invalidate a pending commit.
	// Why the most recent fetch for a field threw, for diagnostics only — never rendered. The
	// blur-commit path cannot read fieldErrors (generation-gated by design), so this is how it names the
	// cause instead of reporting a bare "no rows".
	// Tagged with the generation that produced it. Recorded BELOW before the generation check on
	// purpose, so a SUPERSEDED request's failure lands here too — which the commit's own fetch path
	// needs and the synchronous NoPendingLookup guard must not trust. Two consumers, two
	// requirements: one wants the message whatever its age, the other only a CURRENT failure.
	const lastFetchFailure = ref<Record<string, { message: string; generation: number; kind: FetchFailureKind }>>({});
	const autocompleteUserRevision = ref<Record<string, number>>({});
	// Bumped ONLY when a dependency reset invalidates this field's context. Deliberately separate from
	// fieldRequestGenerations, which is also bumped by Vuetify's own post-blur update:search("") — a
	// benign event that a blur-triggered commit MUST tolerate. One counter cannot mean both "the user's
	// dependency changed under me" and "Vuetify tidied up after blur"; gating the commit on the shared
	// counter would make it skip in the very repro it exists to fix.
	const autocompleteContextEpoch = ref<Record<string, number>>({});

	/** Invalidate any in-flight autocomplete commit for these fields. */
	const invalidateAutocompleteContexts = (fieldKeys: string[]) => {
		for (const fieldKey of new Set(fieldKeys)) {
			autocompleteContextEpoch.value[fieldKey] = (autocompleteContextEpoch.value[fieldKey] || 0) + 1;
		}
	};
	const noteAutocompleteUserInteraction = (fieldKey: string) => {
		autocompleteUserRevision.value[fieldKey] = (autocompleteUserRevision.value[fieldKey] || 0) + 1;
	};

	const DEFAULT_MIN_SEARCH_CHARS = 2;
	/**
	 * How many characters must be typed before a search is worth issuing.
	 *
	 * The backing MCP tool enforces the field's own `validation.minLength`, so every keystroke below it
	 * fired a request that could only be rejected — and the rejection rendered as a red field error
	 * WHILE THE USER WAS STILL TYPING. DEFAULT is a floor and there is deliberately no ceiling: a cap
	 * would let a field whose real minimum is above it fire doomed requests again, which is a milder
	 * version of the very bug this closes. `validation.minLength` constrains the committed VALUE rather
	 * than the query, so a field that genuinely wants a narrower search declares
	 * `mcpDataSource.minSearchChars` instead of having one imposed on it.
	 *
	 * `validation.pattern` is deliberately NOT consulted: a legitimate prefix of a valid value fails the
	 * full-value pattern, so gating on it would block real input.
	 */
	/**
	 * The threshold can only ever be RAISED. `mcpDataSource.minSearchChars` is an escape hatch for a field
	 * whose search needs MORE characters than its own `validation.minLength` implies — it does not license
	 * fewer. Taking it as an override in both directions meant a field with `minLength: 50` and
	 * `minSearchChars: 3` searched at 3, firing exactly the doomed requests this ticket removes.
	 *
	 * So all three inputs are maxed together, and the default is an absolute floor. Pinned by "still
	 * refuses to go below the default floor".
	 */
	/**
	 * Every candidate is normalized BEFORE it is maxed, because `Math.max` coerces rather than
	 * validates. A schema declaring `minLength` as a non-numeric string or an object yields NaN, and
	 * `length < NaN` is false — which DISABLES the gate and reinstates the very bug this closes, while
	 * rendering "Type at least NaN characters to search" to the user. `POSITIVE_INFINITY` made the
	 * field permanently unsearchable, and a float rendered "Type at least 5.9 characters". Flooring
	 * keeps the threshold an integer so the gate and the copy can never disagree.
	 *
	 * An unusable declaration contributes 0, which the floor below then absorbs — it can only ever
	 * fall back to the default, never disable the gate. Pinned by the "normalizes %s" and "falls back
	 * to the 2-character floor" cases in `userInputFormRendererMinSearchLength.spec.ts`.
	 */
	const toSearchCharCandidate = (declared: unknown): number => {
		const parsed = Number(declared);
		return Number.isFinite(parsed) ? Math.floor(parsed) : 0;
	};
	/**
	 * Minimums the backend has STATED for a field's lookup, by rejecting a query as too short (LSP-18801
	 * rework). The schema-derived gate is only as good as the pack: a field that declares no minimum falls
	 * back to 2 while its tool may enforce 4, and every keystroke in between used to paint a red error.
	 * The rejection names the real minimum, so it is kept for the life of this renderer and gates the next
	 * keystroke. Raise-only, like every other candidate below.
	 *
	 * Keyed by the lookup as well as the field, so a schema swap that points the same key at a different
	 * tool or search parameter does not inherit a rule that belongs to another backend, and a late
	 * response can only ever teach the rule of the lookup it came from.
	 */
	const learnedMinSearchChars = ref<Record<string, number>>({});
	const learnedMinimumKey = (field: FieldDefinition): string =>
		[field.key, field.mcpDataSource?.toolName, field.mcpDataSource?.platformCodeName, field.mcpDataSource?.searchParam].join("\u0000");
	const getMinSearchChars = (field: FieldDefinition): number =>
		Math.max(
			toSearchCharCandidate(field.mcpDataSource?.minSearchChars),
			toSearchCharCandidate(field.validation?.minLength),
			toSearchCharCandidate(learnedMinSearchChars.value[learnedMinimumKey(field)]),
			DEFAULT_MIN_SEARCH_CHARS
		);
	const searchDebounceTimers = ref<Record<string, ReturnType<typeof setTimeout>>>({});

	const extractRawValue = (val: any): any => {
		if (val && typeof val === "object" && "value" in val && "fieldLabel" in val) {
			return val.value;
		}
		return val;
	};

	const seedMcpOptionsFromModelValue = () => {
		if (!props.modelValue) return;
		for (const field of props.schema.fields) {
			const mv = props.modelValue[field.key];
			if (!mv || typeof mv !== "object" || !("value" in mv) || !("fieldLabel" in mv)) continue;
			if (!field.mcpDataSource) continue;

			const enriched = mv as {
				value: unknown;
				displayLabel?: string;
				mcpSource?: { record?: Record<string, unknown>; inputParams?: Record<string, unknown> };
			};
			if (enriched.value == null || enriched.value === "") continue;

			const syntheticOption: McpOptionWithProvenance = {
				label: enriched.displayLabel || String(enriched.value),
				value: enriched.value as string | number | boolean
			};
			if (enriched.mcpSource?.record) {
				syntheticOption.record = enriched.mcpSource.record;
			}
			if (enriched.mcpSource?.inputParams) {
				syntheticOption._mcpRequestParams = enriched.mcpSource.inputParams;
			}

			const existing = mcpOptions.value[field.key] || [];
			if (!existing.some((o) => o.value === syntheticOption.value)) {
				mcpOptions.value[field.key] = [...existing, syntheticOption];
			}
		}
	};

	const initializeFormValues = () => {
		// EVERY reset invalidates in-flight autocomplete commits, and it is done HERE rather than at the
		// call sites because there are three of them (schema watcher, modelValue watcher, onMounted) and
		// missing one silently reintroduces the bug — which is exactly what happened: the schema watcher
		// was fixed and the modelValue watcher was not. The reset writes null, which for a not-yet-committed
		// field equals the selection a commit captured before its await, so none of the commit's own guards
		// can see it and a stale option lands in the freshly reset form.
		invalidateAutocompleteContexts([...Object.keys(autocompleteContextEpoch.value), ...props.schema.fields.map((field) => field.key)]);
		const values: Record<string, any> = {};
		for (const field of props.schema.fields) {
			if (props.modelValue && props.modelValue[field.key] !== undefined) {
				values[field.key] = extractRawValue(props.modelValue[field.key]);
			} else if (field.defaultValue !== undefined) {
				values[field.key] = field.defaultValue;
			} else if (field.type === "checkbox") {
				values[field.key] = false;
			} else if (field.type === "multiselect" || field.type === "multi_autocomplete") {
				values[field.key] = [];
			} else {
				values[field.key] = null;
			}

			if (field.type === "autocomplete" || field.type === "multi_autocomplete") {
				autocompleteSearchText.value[field.key] = "";
			}
		}
		// A reset blanks every field, so a warning still pointing at one is the mirror image of the bug
		// this ticket fixes: the screen and the model disagreeing. Nothing else clears these for an
		// autocomplete field — fetchAllMcpOptions skips both autocomplete types, and the modelValue
		// watcher never calls it — so a pack reload left "Please check these entries" over a field
		// that had already been silently emptied, accusing the user of an entry no longer on screen.
		unresolvedEntries.value = {};
		formValues.value = values;
	};

	// visibleFields delegates to the shared isUserInputFieldVisible predicate (fieldVisibility.ts)
	// so the render layer and the Run-Report gate (useRunAgentUserInput.allUserInputValid) always
	// agree on which fields are "visible". Do not duplicate the operator logic here. (guards LSP-16496)
	const visibleFields = computed(() => {
		return props.schema.fields.filter((field) => isUserInputFieldVisible(field, formValues.value));
	});

	const isFieldDisabled = (field: FieldDefinition): boolean => {
		if (!field.mcpDataSource?.dependsOn?.length) return false;
		for (const depKey of field.mcpDataSource.dependsOn) {
			const depValue = formValues.value[depKey];
			if (depValue === null || depValue === undefined || depValue === "") {
				return true;
			}
			if (Array.isArray(depValue) && depValue.length === 0) {
				return true;
			}
		}
		return false;
	};

	const getFieldOptions = (field: FieldDefinition): UserInputFieldOption[] => {
		if (field.mcpDataSource) {
			return mcpOptions.value[field.key] || [];
		}
		return (field.options || []) as UserInputFieldOption[];
	};

	const getNoDataText = (field: FieldDefinition): string => {
		if (loadingFields.value[field.key]) {
			return field.type === "autocomplete" || field.type === "multi_autocomplete" ? "Searching..." : "Loading...";
		}
		if (isFieldDisabled(field)) {
			const deps = field.mcpDataSource?.dependsOn || [];
			const depLabels = deps.map((key) => getFieldLabelByKey(key)).join(", ");
			return `Select ${depLabels} first`;
		}
		if (fieldErrors.value[field.key]) {
			return "Error loading options";
		}
		if (field.type === "autocomplete" || field.type === "multi_autocomplete") {
			const searchText = autocompleteSearchText.value[field.key];
			const minSearchChars = getMinSearchChars(field);
			if (!searchText || searchText.length < minSearchChars) {
				return `Type at least ${minSearchChars} characters to search`;
			}
			return "No results found";
		}
		return "No options available";
	};

	const getFieldLabelByKey = (key: string): string => {
		const field = props.schema.fields.find((f) => f.key === key);
		return field?.label || key;
	};

	const getRequiredLabel = (field: FieldDefinition): string => {
		return field.validation?.required ? `${field.label} (Required)` : field.label;
	};

	const getInputType = (type: string): string => {
		switch (type) {
			case "email":
				return "email";
			case "phone":
				return "tel";
			case "url":
				return "url";
			default:
				return "text";
		}
	};

	const getValidationRules = (field: FieldDefinition) => {
		if (props.validationEnabled === false) return [];
		const rules: ((v: any) => boolean | string)[] = [];
		const validation = field.validation;

		if (validation?.required) {
			// Same emptiness test as useRunAgentUserInput.isFieldFilled, so the red field and the Run gate
			// agree: whitespace-only text and an unticked required checkbox are not an answer (LSP-21386).
			rules.push((v: any) => {
				if (v === null || v === undefined) return `${field.label} is required`;
				if (typeof v === "string" && v.trim() === "") return `${field.label} is required`;
				if (Array.isArray(v) && v.length === 0) return `${field.label} is required`;
				if (field.type === "checkbox" && v === false) return `${field.label} is required`;
				return true;
			});
		}
		if (validation?.minLength !== undefined) {
			rules.push((v: any) => {
				if (!v) return true;
				return String(v).length >= validation.minLength! || `Minimum ${validation.minLength} characters`;
			});
		}
		if (validation?.maxLength !== undefined) {
			rules.push((v: any) => {
				if (!v) return true;
				return String(v).length <= validation.maxLength! || `Maximum ${validation.maxLength} characters`;
			});
		}
		if (validation?.min !== undefined) {
			rules.push((v: any) => {
				if (v === null || v === undefined || v === "") return true;
				return Number(v) >= validation.min! || `Minimum value is ${validation.min}`;
			});
		}
		if (validation?.max !== undefined) {
			rules.push((v: any) => {
				if (v === null || v === undefined || v === "") return true;
				return Number(v) <= validation.max! || `Maximum value is ${validation.max}`;
			});
		}
		if (validation?.pattern) {
			const regex = new RegExp(validation.pattern);
			rules.push((v: any) => {
				if (!v) return true;
				return regex.test(String(v)) || validation.patternMessage || "Invalid format";
			});
		}
		if (field.type === "number") {
			// Number fields render as type="text" (see template), so non-numeric characters such as
			// 'e' reach the model as strings. Without a numeric-format rule they would pass the
			// required-only Run-Report gate. Reuse BasicNumberValidation (same rule WebFormV2 applies
			// to its Number fields) so invalid numeric text is rejected. (guards LSP-16496)
			rules.push((v: any) => isNumericFieldValueValid(v) || "Please enter a valid number");
		}
		if (field.type === "url") {
			rules.push((v: any) => {
				if (!v) return true;
				try {
					new URL(String(v));
					return true;
				} catch {
					return "Invalid URL format";
				}
			});
		}
		return rules;
	};

	const setMcpOptions = (fieldKey: string, newOptions: UserInputFieldOption[]) => {
		const currentValue = formValues.value[fieldKey];
		if (currentValue != null && currentValue !== "") {
			const selectedValues = new Set(Array.isArray(currentValue) ? currentValue : [currentValue]);
			const previous = mcpOptions.value[fieldKey] || [];
			const selectedPrevious = previous.filter((o) => selectedValues.has(o.value) && !newOptions.some((n) => n.value === o.value));
			mcpOptions.value[fieldKey] = [...selectedPrevious, ...newOptions];
		} else {
			mcpOptions.value[fieldKey] = newOptions;
		}
	};

	const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
		return path.split(".").reduce((current: unknown, key: string) => {
			if (current && typeof current === "object" && key in (current as Record<string, unknown>)) {
				return (current as Record<string, unknown>)[key];
			}
			return undefined;
		}, obj);
	};

	const getFieldByKey = (key: string): FieldDefinition | undefined => {
		return props.schema.fields.find((f) => f.key === key);
	};

	const getOptionsForFieldKey = (fieldKey: string): UserInputFieldOption[] => {
		const field = getFieldByKey(fieldKey);
		if (!field) return [];
		if (field.mcpDataSource) return mcpOptions.value[fieldKey] || [];
		return (field.options || []) as UserInputFieldOption[];
	};

	type InputParamMapping = NonNullable<NonNullable<FieldDefinition["mcpDataSource"]>["inputParamMappings"]>[number];

	const resolveParamMappingValue = (mapping: InputParamMapping): unknown => {
		const sourceValue = formValues.value[mapping.sourceField];
		if (sourceValue === null || sourceValue === undefined || sourceValue === "") return undefined;

		if (!mapping.sourceValuePath) return sourceValue;

		const selectedValues = Array.isArray(sourceValue) ? sourceValue : [sourceValue];
		const options = getOptionsForFieldKey(mapping.sourceField);

		const extracted = selectedValues
			.map((v) => {
				const opt = options.find((o) => o.value === v);
				if (!opt?.record) return undefined;
				return getNestedValue(opt.record, mapping.sourceValuePath);
			})
			.filter((v) => v !== undefined && v !== null && v !== "");

		if (!extracted.length) return undefined;
		return Array.isArray(sourceValue) ? extracted : extracted[0];
	};

	/**
	 * Did the lookup REFUSE the entry, as opposed to failing to answer (LSP-19462)? scribe-api maps an MCP
	 * tool's own error result to 400 (TaskRequirementController.getUserInputOptions), which is where
	 * "Job ID must be at most 8 characters" comes from; a timeout, a 5xx or a network failure never is.
	 * Search-driven only: a 400 on a field's initial option load is a configuration problem, not
	 * something the user typed, so it stays in the "could not be loaded" banner.
	 */
	const isRejectedLookupInput = (error: unknown, searchText: string | undefined): boolean =>
		searchText !== undefined && (error as { status?: unknown } | null | undefined)?.status === 400;

	/**
	 * The minimum a rejection states for the query, when the query was shorter than it — otherwise null
	 * (LSP-18801 rework). "Job ID must be at least 4 characters" at 2 characters means "keep typing", not
	 * "this entry is wrong", so it must not be shown as a field error the way LSP-19462 shows other refusals.
	 *
	 * Deliberately narrow. Only a rejection isRejectedLookupInput already classifies (a search-driven 400),
	 * only a SINGLE message (apiError.ts joins several validation messages with a newline, and a minimum
	 * sitting next to some other refusal is not safe to swallow), and only when the stated minimum is
	 * longer than what was actually sent — "at least 4" about a 5-character query is not explaining that
	 * query, so it stays a rejection.
	 */
	const statedMinimumFromRejection = (error: unknown, searchText: string | undefined): number | null => {
		if (!isRejectedLookupInput(error, searchText)) return null;
		const message = error instanceof Error ? error.message : String(error);
		if (message.includes("\n")) return null;
		const match = /\b(?:at least|minimum(?: of)?)\s+(\d+)\s+char/i.exec(message);
		if (!match) return null;
		const statedMinimum = Number(match[1]);
		return Number.isSafeInteger(statedMinimum) && (searchText ?? "").trim().length < statedMinimum ? statedMinimum : null;
	};

	/**
	 * The rejection in the FIELD'S words, never the backend's (LSP-19462). Derived from the field's own
	 * validation config against what was typed, so it can name the limit that was crossed; when no local
	 * constraint explains the refusal it says so plainly rather than guessing.
	 */
	const describeRejectedLookupInput = (field: FieldDefinition, searchText: string): string => {
		const validation = field.validation;
		const entry = searchText.trim();
		if (validation?.maxLength !== undefined && entry.length > validation.maxLength) {
			return `${field.label} can be up to ${validation.maxLength} characters.`;
		}
		if (validation?.minLength !== undefined && entry.length < validation.minLength) {
			return `${field.label} must be at least ${validation.minLength} characters.`;
		}
		if (validation?.pattern && validation.patternMessage) {
			try {
				if (!new RegExp(validation.pattern).test(entry)) return validation.patternMessage;
			} catch {
				// An invalid pattern in the schema cannot explain anything; fall through to the generic copy.
			}
		}
		return `That doesn't look like a valid ${field.label}. Please check it and try again.`;
	};

	const fetchMcpFieldOptions = async (field: FieldDefinition, searchText?: string) => {
		if (!field.mcpDataSource) return;
		const requestGeneration = (fieldRequestGenerations.value[field.key] || 0) + 1;
		fieldRequestGenerations.value[field.key] = requestGeneration;
		if (isFieldDisabled(field)) {
			loadingFields.value[field.key] = false;
			return;
		}

		const { toolName, platformCodeName, labelTemplate, valueField, inputParams, inputParamMappings, searchParam } = field.mcpDataSource;
		// Captured BEFORE the await: a rejection teaches the minimum of the lookup that was actually asked, even if
		// the field's lookup config changes while the request is in flight (LSP-18801).
		const minimumKey = learnedMinimumKey(field);

		const params: Record<string, unknown> = {};
		if (inputParams) {
			for (const [key, value] of Object.entries(inputParams)) {
				if (value === null || value === undefined) continue;
				if (typeof value === "string" && !value.trim()) continue;
				params[key] = value;
			}
		}

		if (searchParam && searchText !== undefined) {
			params[searchParam] = searchText;
		}

		if (inputParamMappings) {
			for (const mapping of inputParamMappings) {
				const mappedValue = resolveParamMappingValue(mapping);
				if (mappedValue !== undefined) {
					params[mapping.paramName] = mappedValue;
				}
			}
		}

		loadingFields.value[field.key] = true;
		// fieldErrors is deliberately NOT cleared here (LSP-19462). Doing so blanked the message the instant a new
		// request STARTED, and painted it again ~RTT later — the blink the ticket describes. A failure is replaced
		// only by the next RESULT for this field: success clears it below, another failure overwrites it.
		delete unresolvedEntries.value[field.key];

		try {
			const options = await fetchUserInputOptions(toolName, platformCodeName, labelTemplate, valueField, params);
			const optionsWithProvenance: McpOptionWithProvenance[] = options.map((option) => ({
				...option,
				_mcpRequestParams: params
			}));
			if (fieldRequestGenerations.value[field.key] === requestGeneration) {
				setMcpOptions(field.key, optionsWithProvenance);
				// The lookup accepted this text, so whatever it said about the previous text is over (LSP-19462).
				delete fieldErrors.value[field.key];
			}
			// A SUCCESS MAY ONLY CLEAR ITS OWN FAILURE OR AN OLDER ONE. This delete used to be unconditional,
			// while the write three lines below in the catch is generation-stamped — so a slow request that
			// eventually succeeded could wipe a NEWER request's real failure. The commit path reads this record
			// under `generation === fieldRequestGenerations[key]`, so erasing the current generation's entry does
			// not degrade gracefully: it disables the LookupFailed branch entirely, and the commit falls through
			// to "no unambiguous match" — clearing a committed value and blaming the user's entry for a backend
			// outage. Reported independently by two reviewers.
			//
			// Deliberately NOT folded into the generation check above: a success that lost the race must still
			// clear the failure IT recorded on a previous attempt, so the test is "not newer than me", not
			// "I am current".
			const recordedFailure = lastFetchFailure.value[field.key];
			if (recordedFailure === undefined || recordedFailure.generation <= requestGeneration) {
				delete lastFetchFailure.value[field.key];
			}
			// Returned regardless of who won the generation race. The blur-commit path needs the rows it
			// asked for even when a newer request (Vuetify's own `update:search("")` on blur) has already
			// superseded the shared store — see commitTypedAutocompleteValue.
			return optionsWithProvenance;
		} catch (error) {
			// Recorded BEFORE the generation check so the blur-commit path can say WHY it gave up. That
			// path deliberately loses the generation race (see commitTypedAutocompleteValue), so it can
			// never learn about the failure from fieldErrors or from this console.error — both of which
			// stay generation-gated on purpose, because LSP-18550 requires a superseded request's failure
			// to stay invisible to the USER. This is a diagnostic channel only; it surfaces nothing.
			// SAME "NOT NEWER THAN ME" TEST AS THE SUCCESS-SIDE CLEAR, and for the same reason. This write was
			// unconditional while the clear was guarded, which is half a symmetry: an older request whose
			// failure arrives LATE would stamp its smaller generation over a newer request's record. The commit
			// path matches on `generation === fieldRequestGenerations[key]` exactly, so the clobbered record
			// then fails that match and the commit falls through to reportUnresolvedEntry — nulling a value the
			// user had legitimately committed. That is this ticket's own bug, reintroduced from the failure side.
			//
			// Still written BEFORE the generation check below, which is deliberate and unchanged: the
			// blur-commit path loses the generation race on purpose, so this is the only channel that can tell
			// it WHY a lookup gave up. The guard is about the record already there, not about being current.
			const statedMinimum = statedMinimumFromRejection(error, searchText);
			const failureKind: FetchFailureKind =
				statedMinimum !== null ? "below-minimum" : isRejectedLookupInput(error, searchText) ? "rejected-input" : "unavailable";
			const existingFailure = lastFetchFailure.value[field.key];
			if (existingFailure === undefined || existingFailure.generation <= requestGeneration) {
				lastFetchFailure.value[field.key] = {
					message: error instanceof Error ? error.message : String(error),
					generation: requestGeneration,
					kind: failureKind
				};
			}
			// LSP-18801 rework: the backend told us this field's real minimum. Learned even when superseded —
			// a newer keystroke does not make the rule any less true — and logged at warn, not error: this is an
			// expected answer, and console.error is what RUM records as a production error.
			if (statedMinimum !== null) {
				learnedMinSearchChars.value[minimumKey] = Math.max(learnedMinSearchChars.value[minimumKey] || 0, statedMinimum);
				console.warn(
					`[autocomplete-search] Lookup rejected a too-short query: Field=${field.key}, QueryLength=${(searchText ?? "").trim().length}, StatedMinimum=${statedMinimum}, Current=${fieldRequestGenerations.value[field.key] === requestGeneration}, Message=${error instanceof Error ? error.message : String(error)}`
				);
			}
			if (fieldRequestGenerations.value[field.key] !== requestGeneration) return;
			if (failureKind === "below-minimum") {
				// Quiet, exactly as if the gate had caught it: no field error, no banner, no options. Whatever
				// fieldErrors held was about earlier text, and the lookup did answer, so it no longer holds.
				setMcpOptions(field.key, []);
				delete fieldErrors.value[field.key];
				return;
			}
			console.error(`Failed to fetch options for ${field.key}:`, error);
			setMcpOptions(field.key, []);
			fieldErrors.value[field.key] =
				failureKind === "rejected-input"
					? { kind: "rejected-input", message: describeRejectedLookupInput(field, searchText ?? "") }
					: { kind: "unavailable", message: error instanceof Error ? error.message : "Failed to load options" };
		} finally {
			if (fieldRequestGenerations.value[field.key] === requestGeneration) loadingFields.value[field.key] = false;
		}
	};

	const handleAutocompleteSearch = (field: FieldDefinition, searchText: string | null) => {
		const fieldKey = field.key;
		fieldRequestGenerations.value[fieldKey] = (fieldRequestGenerations.value[fieldKey] || 0) + 1;
		autocompleteSearchText.value[fieldKey] = searchText || "";
		// A non-empty query is unambiguously the user. An empty one is NOT attributable — it is Vuetify
		// clearing up after blur, or the user backspacing to nothing, and the two are indistinguishable
		// here — so it does not advance the revision; the commit's trigger decides how to read it instead.
		if (searchText) noteAutocompleteUserInteraction(fieldKey);

		// fieldErrors is NOT cleared on a search event (LSP-19462): that erased a rejection on the very next
		// keystroke, before the user could read it. It is replaced when the next lookup RESULT arrives.
		// Only on a NON-EMPTY query, for the same reason noteAutocompleteUserInteraction ignores the empty
		// one: Vuetify emits update:search("") as part of its own blur handling, AFTER the commit's
		// synchronous half has already written a warning. Clearing on every emit therefore erased the
		// explanation on the blur path — the reported path. A real fresh attempt always has text.
		//
		// It must also stay OUT of the `if (fieldErrors[fieldKey])` guard above: reports no longer set
		// fieldErrors, so hanging it there made it dead code and the warning outlived its entry.
		//
		// AN ECHO OF THE COMMITTED VALUE IS NOT A FRESH ATTEMPT. `:search` is bound one-way to our own
		// `autocompleteSearchText`, so anything Vuetify writes into its search comes straight back here as
		// an `update:search` — and it writes the SELECTED item's title, not just the empty string handled
		// above. So merely focusing the field can emit a non-empty query identical to the value already
		// committed. Treating that as user intent deleted the unresolved warning and lifted the proceed
		// block, letting Run submit the value the user had already tried to replace.
		//
		// Compared the way findUnambiguousOption compares, so a label/value echo is caught either way.
		if (searchText && !echoesCommittedValue(field, searchText)) {
			delete unresolvedEntries.value[fieldKey];
		}

		if (searchDebounceTimers.value[fieldKey]) {
			clearTimeout(searchDebounceTimers.value[fieldKey]);
			// Must also drop the entry, not just cancel the timer. The short/empty-search path below returns
			// without scheduling a replacement, and the commit's NoPendingLookup gate reads this map as
			// "a lookup is pending" — a cancelled timer left behind makes that read a lie.
			delete searchDebounceTimers.value[fieldKey];
		}

		// ONE notion of "the query", measured and sent. The gate used to measure the RAW text while the
		// fetch below sent the trimmed one, so a padded entry could clear the field minimum on screen
		// and still send a below-minimum query — which the MCP tool rejects, painting a field error
		// mid-entry. That is the keystroke-validation symptom this ticket set out to remove, so a fix
		// for one half of this pair reintroduced it through the other.
		const query = (searchText ?? "").trim();
		// AND AN ECHO MUST NOT SCHEDULE A LOOKUP EITHER. Skipping only the warning delete above was half a
		// fix: production labels clear getMinSearchChars, so the echo still debounced a fetch, and
		// fetchMcpFieldOptions drops unresolvedEntries unconditionally at its start — so the block came
		// back off by the other route, ~300ms later. There is also nothing to look up: the echoed text IS
		// the committed value, already resolved.
		if (query && echoesCommittedValue(field, query)) {
			loadingFields.value[fieldKey] = false;
			return;
		}
		if (!query || query.length < getMinSearchChars(field)) {
			loadingFields.value[fieldKey] = false;
			// A rejection of earlier text cannot be replaced by a result any more — no lookup fires below the
			// threshold — so it would sit under the field describing text the user already deleted (LSP-18801).
			// Only on a NON-EMPTY query: the empty emit is Vuetify's own post-blur tidy-up (LSP-19462). An
			// `unavailable` failure is about the backend, not the text, so it stays.
			if (query && fieldErrors.value[fieldKey]?.kind === "rejected-input") delete fieldErrors.value[fieldKey];
			const currentValue = formValues.value[fieldKey];
			const hasSelectedValue = Array.isArray(currentValue)
				? currentValue.length > 0
				: currentValue !== null && currentValue !== undefined && currentValue !== "";
			// Vuetify clears the search text after a selection. Keep fetched sibling
			// options available while the selected value remains present.
			if (hasSelectedValue) return;
			setMcpOptions(fieldKey, []);
			return;
		}

		searchDebounceTimers.value[fieldKey] = setTimeout(() => {
			delete searchDebounceTimers.value[fieldKey];
			// The SAME query the gate measured. The commit path resolves trimmed text too, so a padded
			// paste that finished its debounce before blur no longer leaves the commit judging options
			// fetched for a query it was not asking about.
			fetchMcpFieldOptions(field, query);
		}, 300);
	};

	/**
	 * The one option a typed/pasted value unambiguously names, or undefined.
	 *
	 * Matching against both `label` and `value` because a job number is the option's value while a
	 * customer name is its label, and requiring a UNIQUE match keeps an ambiguous entry from being
	 * silently resolved to the wrong record.
	 */
	const findUnambiguousOption = (
		field: FieldDefinition,
		text: string,
		candidates?: UserInputFieldOption[]
	): UserInputFieldOption | undefined => {
		const needle = text.trim().toLowerCase();
		if (!needle) return undefined;
		const matches = (candidates ?? getFieldOptions(field)).filter(
			(option) =>
				String(option.label ?? "")
					.trim()
					.toLowerCase() === needle ||
				String(option.value ?? "")
					.trim()
					.toLowerCase() === needle
		);
		return matches.length === 1 ? matches[0] : undefined;
	};

	/**
	 * Does `text` just repeat the value already committed for this field?
	 *
	 * Vuetify echoes the selected item's title back through `update:search`, so a non-empty query is NOT
	 * on its own evidence of user intent — see the note at the `update:search` handler.
	 */
	const echoesCommittedValue = (field: FieldDefinition, text: string): boolean => {
		const committed = formValues.value[field.key];
		if (committed === undefined || committed === null || committed === "") return false;
		const needle = String(text).trim().toLowerCase();
		if (!needle) return false;
		const committedOption = getFieldOptions(field).find((option) => String(option.value ?? "").trim() === String(committed).trim());
		const committedLabel = String(committedOption?.label ?? committed)
			.trim()
			.toLowerCase();
		return needle === committedLabel || needle === String(committed).trim().toLowerCase();
	};

	/** Commit `searchText` if the given options (or the loaded ones) resolve it unambiguously. */
	const applyUnambiguousMatch = (field: FieldDefinition, searchText: string, candidates?: UserInputFieldOption[]): boolean => {
		const match = findUnambiguousOption(field, searchText, candidates);
		if (!match) return false;
		// The entry resolved, so any "please check this entry" about it is now obsolete. Every early return
		// below is also a success (the value already equals what the match would set), so this clears first.
		delete unresolvedEntries.value[field.key];

		if (field.type === "multi_autocomplete") {
			const current = Array.isArray(formValues.value[field.key]) ? [...formValues.value[field.key]] : [];
			if (current.includes(match.value)) return true;
			formValues.value[field.key] = [...current, match.value];
		} else {
			if (formValues.value[field.key] === match.value) return true;
			formValues.value[field.key] = match.value;
		}
		// Mirror what selecting from the menu does, so dependent fields refetch either way.
		handleFieldChange(field);
		return true;
	};

	/**
	 * Commit a typed or pasted value when the field loses focus or Enter is pressed (LSP-16816).
	 *
	 * `v-autocomplete` only writes its model when an item is SELECTED — typed text lives in `search`,
	 * which Vuetify discards on blur. A customer who pasted a valid job number and clicked away was
	 * therefore left with an empty `formValues` entry and a failed integration run, with the valid
	 * value still on screen. Resolving to a fetched option instead of committing the raw string is
	 * deliberate: these fields carry an option `value` that the MCP tool resolves against, so a
	 * free-text commit would just move the failure downstream.
	 *
	 * EVERY exit below logs. This is a silent-failure fix, and root `CLAUDE.md` ("Diagnostic logging
	 * when fixing silent failures") requires one line per fail-condition in the gate — not just the one
	 * that caused the original report — so the next "my value did not take" does not need the same
	 * reconstruction this one did.
	 */
	const commitTypedAutocompleteValue = async (field: FieldDefinition, trigger: "blur" | "enter" = "blur"): Promise<void> => {
		if (field.type !== "autocomplete" && field.type !== "multi_autocomplete") return;
		const searchText = (autocompleteSearchText.value[field.key] || "").trim();
		// Snapshotted so the post-await guards can tell what the USER did during the lookup from what
		// Vuetify did as part of its own blur handling.
		const selectionBeforeFetch = JSON.stringify(formValues.value[field.key] ?? null);
		const revisionBeforeFetch = autocompleteUserRevision.value[field.key] || 0;
		const contextEpochBeforeFetch = autocompleteContextEpoch.value[field.key] || 0;
		const skip = (reason: string, detail = "") =>
			console.warn(`[autocomplete-commit] Skipped: Field=${field.key}, Reason=${reason}${detail ? `, ${detail}` : ""}`);

		/**
		 * A commit outcome the user can DO something about: their entry could not be resolved. Those exits
		 * only logged, so a pasted ambiguous or unknown value produced no visible signal at all — the
		 * silent-failure class this ticket is about, just moved one step later.
		 *
		 * Clearing the stale value is deliberate and is the part worth arguing with. When a field already
		 * holds a committed value and the user types a REPLACEMENT we cannot resolve, keeping the old value
		 * means the form submits something different from what is on screen — and validity is computed from
		 * the value, not the display, so nothing else stops it. Clearing makes the screen and the model agree
		 * and lets the normal required-field validation refuse the run.
		 */
		/**
		 * Did the user, or a dependency reset, move on while we were awaiting the lookup? Consulted by the
		 * FAILURE path too: reporting an error and clearing the value there would undo a menu pick or a retype
		 * the success path is careful to leave alone, purely because the fetch happened to fail afterwards.
		 */
		const commitWasSuperseded = (): string | null => {
			if ((autocompleteContextEpoch.value[field.key] || 0) !== contextEpochBeforeFetch) return "DependencyContextChanged";
			const latestText = (autocompleteSearchText.value[field.key] || "").trim();
			if (latestText !== searchText && (trigger === "enter" || latestText)) return "SearchTextChanged";
			if ((autocompleteUserRevision.value[field.key] || 0) !== revisionBeforeFetch) return "UserInteractedDuringLookup";
			if (JSON.stringify(formValues.value[field.key] ?? null) !== selectionBeforeFetch) return "SelectionChangedDuringLookup";
			return null;
		};

		/**
		 * Is this field still on screen? One definition, used by the report guard below and by the single
		 * writer, so the two cannot drift apart.
		 */
		const fieldIsVisible = () => visibleFields.value.some((candidate) => candidate.key === field.key);
		/**
		 * THE ONLY writer of unresolvedEntries, so the visibility rule cannot be honoured by one caller and
		 * skipped by another.
		 *
		 * Pruning on the visibility TRANSITION was not enough, and a reviewer caught why: hiding a field does
		 * not invalidate an in-flight commit, so a paste-then-blur lookup that settles AFTER the field went
		 * off screen wrote the warning straight back and proceed stayed blocked over a field the user can
		 * neither see nor fix. The transition prune stays — it handles an entry recorded while visible — and
		 * this closes the late write. Two guards because there are two orderings, not for belt and braces.
		 *
		 * Keyed on `visibleFields`, the same computed that drives rendering and the prune, so "visible" has one
		 * definition here. The admin copy has `isUserInputFieldVisible` to call; this package inlines the same
		 * rule because it cannot import from `apps/`, and routing through the computed keeps both copies
		 * expressing it identically.
		 */
		const recordUnresolvedEntry = (message: string) => {
			if (!fieldIsVisible()) {
				console.warn(`[user-input] Not recording an unresolved entry for a hidden field: FieldKey=${field.key}`);
				return;
			}
			unresolvedEntries.value[field.key] = message;
		};
		const reportUnresolvedEntry = (reason: string, detail: string, message: string) => {
			// BEFORE ANY MUTATION, not just before the message. Guarding only the write was my own mistake
			// and it made this case WORSE: this function nulls the value and runs handleFieldChange — which
			// clears dependents — and only then records the warning. So on a hidden field the value and its
			// dependents were destroyed, nothing was recorded, and flush() then PERMITTED proceed with the
			// mutilated payload and no explanation. Before the write guard, the warning at least blocked it.
			//
			// A hidden field has no contradiction to resolve: the text that provoked this is off screen, and
			// its committed value is whatever the user chose while it WAS visible. Destroying that to explain
			// something they cannot see is the worst of the three options.
			if (!fieldIsVisible()) {
				skip("HiddenField", `${detail}, Reason=${reason}, NotReportedOrCleared=true`);
				return;
			}
			// The field is committed to something already, and the text is just that value's own label echoed
			// back — Vuetify writes the selected item's title into `search` on focus AND on select
			// (VAutocomplete.js), so this arrives looking like typing. The user replaced nothing.
			//
			// This must be checked HERE and not left to applyUnambiguousMatch, which requires exactly ONE match:
			// when two fetched options share a label, the echo of a value the user just picked resolves
			// AMBIGUOUSLY, so it never reaches that function's already-equals-the-match path and would be cleared
			// while being told to pick from the list they just picked from.
			//
			// echoCandidate must be non-empty: an empty search against a missing label is "" === "", which would
			// swallow the blank-entry exits that legitimately have no text.
			const committedValue = formValues.value[field.key];
			const echoCandidate = searchText.trim().toLowerCase();
			if (
				echoCandidate !== "" &&
				!Array.isArray(committedValue) &&
				committedValue !== null &&
				committedValue !== undefined &&
				committedValue !== ""
			) {
				const committedLabel = getFieldOptions(field).find((option) => option.value === committedValue)?.label;
				const echoesTheCommittedValue =
					(committedLabel !== undefined && String(committedLabel).trim().toLowerCase() === echoCandidate) ||
					String(committedValue).trim().toLowerCase() === echoCandidate;
				if (echoesTheCommittedValue) {
					skip("EchoOfCommittedValue", `SearchText=${searchText}`);
					return;
				}
			}
			skip(reason, detail);
			const existing = formValues.value[field.key];
			// Only a SINGLE-select value is contradicted by the text on screen. multi_autocomplete shows committed
			// chips ALONGSIDE its search box, so an unresolvable extra entry does not contradict them — replacing
			// the array would erase selections the user already made and can still see.
			const isMultiSelect = Array.isArray(existing);
			const hasStaleValue = !isMultiSelect && existing !== null && existing !== undefined && existing !== "";
			if (hasStaleValue) {
				console.warn(`[autocomplete-commit] Cleared stale value: Field=${field.key}, Reason=${reason}`);
				formValues.value[field.key] = null;
				// Dependents were resolved under the value just removed, so they have to go the same way every
				// other clear of this field does. Writing formValues alone left children holding options keyed to
				// a parent that no longer exists.
				handleFieldChange(field);
			}
			// LAST: handleFieldChange clears this field's messages, so setting one before it would erase the
			// only user-visible signal.
			recordUnresolvedEntry(message);
		};

		/**
		 * A LOOKUP failed, as distinct from an entry we judged and could not resolve.
		 *
		 * Keeps whatever is committed and explains why. Two reviewers found the two halves of this one
		 * round apart: clearing a committed value because the BACKEND failed destroys data and blames
		 * the user for an outage, and keeping it silently leaves the screen showing the replacement they
		 * typed while the model holds the old value. Both paths that can hit a failed lookup — the
		 * debounced one and the commit's own — go through here, because a third reviewer then found
		 * that fixing only one of them left the two disagreeing about identical root causes.
		 *
		 * unresolvedEntries rather than fieldErrors: Vuetify's post-blur `update:search("")` deletes
		 * fieldErrors unconditionally, while unresolvedEntries is only cleared on a NON-EMPTY query and
		 * therefore survives the blur that triggered the commit.
		 */
		const reportLookupFailure = (detail: string) => {
			skip("LookupFailed", detail);
			const contradicted = formValues.value[field.key];
			const hasContradictedValue =
				!Array.isArray(contradicted) && contradicted !== null && contradicted !== undefined && contradicted !== "";
			recordUnresolvedEntry(
				hasContradictedValue
					? `We could not look up "${searchText}" just now, so your previous selection is unchanged. Please try again.`
					: `We could not look up "${searchText}" just now. Please try again.`
			);
		};

		/**
		 * The lookup REFUSED the entry (LSP-19462) — the other kind of failed lookup. reportLookupFailure
		 * above keeps the committed value because the backend could not answer; a rejection IS the answer,
		 * about the text on screen, so it is handled like any other entry that could not be resolved: a
		 * contradicted stale value is cleared, and proceed stays blocked through unresolvedEntries. Its
		 * explanation lives under the field rather than in the banner, in the field's own words — and it is
		 * (re)written LAST, because reportUnresolvedEntry may run handleFieldChange, which reads the cleared
		 * value as "the user emptied the field" and drops the field's messages with it.
		 */
		const reportRejectedEntry = (detail: string) => {
			const message = describeRejectedLookupInput(field, searchText);
			reportUnresolvedEntry("LookupRejectedInput", detail, message);
			if (fieldIsVisible()) fieldErrors.value[field.key] = { kind: "rejected-input", message };
		};

		if (!searchText) {
			// Whitespace-only is the user REPLACING the value, not Vuetify tidying up. The gate above trims, so
			// "   " lands here looking identical to the post-blur empty emit — and returning left the old value
			// live behind a field the user can see is blank. The raw text tells them apart: Vuetify emits "".
			const rawSearchText = autocompleteSearchText.value[field.key] || "";
			const existingValue = formValues.value[field.key];
			const whitespaceOnlyOverAValue =
				rawSearchText.length > 0 &&
				field.type !== "multi_autocomplete" &&
				existingValue !== null &&
				existingValue !== undefined &&
				existingValue !== "";
			if (whitespaceOnlyOverAValue) {
				// Cancel the pending lookup first. The search gate is UNTRIMMED, so whitespace long enough for
				// the field's minimum has already scheduled a debounce — and fetchMcpFieldOptions deletes
				// unresolvedEntries as its first act, so letting that timer fire would erase this warning while
				// leaving the value cleared. Enter has no post-blur update:search("") to cancel it either.
				if (searchDebounceTimers.value[field.key]) {
					clearTimeout(searchDebounceTimers.value[field.key]);
					delete searchDebounceTimers.value[field.key];
				}
				reportUnresolvedEntry(
					"WhitespaceOnlyEntry",
					`RawLength=${rawSearchText.length}`,
					"That entry is blank. Please type a value or pick one from the list."
				);
				return;
			}
			// An untouched field blurs on every tab-through; logging there would emit one line per field
			// crossed and bury the exits that mean something.
			// Nothing on screen and nothing committed: whatever the warning was about is gone, so it must
			// not stay pinned to an empty field. Guarded on the value too — a field that still HOLDS a
			// selection keeps its warning, because that is the failed-lookup case where the model and the
			// screen genuinely still disagree.
			// An empty MULTI-select is `[]`, not null — initializeFormValues seeds multi_autocomplete that way.
			// The three scalar checks alone left a chipless multi field unable to clear its own warning: the
			// two reportLookupFailure exits are not type-gated, so a failed lookup pins a warning to a multi
			// field, and blanking the text then found `[]` "non-empty" and kept it — leaving flush() refusing
			// to proceed on a field showing nothing. Found by a reviewer on the escape hatch itself.
			// MULTI-SELECT COUNTS AS HOLDING NOTHING *TO CONTRADICT*, whatever its chips. This is the escape
			// hatch a review found missing: NoUnambiguousMatch is the one unresolved-entry exit not exempted
			// for multi_autocomplete, so a leftover partial query beside a valid chip raises a warning — and
			// with chips present nothing short of a NEW exact match cleared it, because this hatch asked whether
			// the VALUE was empty. Blanking the leftover text did not help. Continue then refused over a field
			// holding a correct selection.
			//
			// Fixed HERE rather than by gating that exit, which is what the review suggested. Gating it would
			// silence a multi entry that genuinely matches nothing — the "unmatched new chip entry silently
			// dropped on Run" medium from an earlier round on this PR — so the report stays and the missing
			// escape is what gets added. Sound for multi and ONLY for multi: chips sit alongside the search box
			// and are additive, so an empty query contradicts none of them. For single-select the text IS the
			// value's replacement, which is why that case still requires the value itself to be empty — and why
			// Vuetify's post-blur update:search("") must not lift a single-select block.
			const holdsNothing =
				field.type === "multi_autocomplete" ||
				existingValue === null ||
				existingValue === undefined ||
				existingValue === "" ||
				(Array.isArray(existingValue) && existingValue.length === 0);
			if (holdsNothing) {
				delete unresolvedEntries.value[field.key];
				// Same for a rejection: the text it was about is gone (LSP-19462).
				delete fieldErrors.value[field.key];
			}
			if (autocompleteUserRevision.value[field.key] !== undefined) skip("NoSearchText");
			return;
		}
		/**
		 * The entry is shorter than the field's minimum — declared, or stated by the backend rejecting it
		 * (LSP-18801). One exit for both, so a too-short entry reads the same whether the gate caught it or the
		 * lookup did. A rejection of EARLIER text is dropped here too: on blur this commit's fetch loses the
		 * generation race to Vuetify's post-blur `update:search("")`, so the fetch cannot clear it itself.
		 */
		const reportTooShortEntry = (extraDetail = "") => {
			if (fieldErrors.value[field.key]?.kind === "rejected-input") delete fieldErrors.value[field.key];
			const tooShortDetail = `Length=${searchText.length}, Required=${getMinSearchChars(field)}${extraDetail}`;
			// The fourth exit in the unresolved-entry class, and the one that mattered most: a user replacing a
			// committed value with something SHORTER than the minimum left the old value in formValues while the
			// field showed the new text, and this exit returned before anything could report or clear it. Run
			// then submitted a value the user could no longer see — the exact failure this ticket closes.
			//
			// Only when there is actually a value being contradicted. An empty field below the minimum is just a
			// half-typed entry, and the field's own "type at least N characters" hint already covers it. Multi-
			// select is excluded for the same reason as the sync exit below: its query survives a pick.
			const existing = formValues.value[field.key];
			const contradictsACommittedValue =
				field.type !== "multi_autocomplete" && existing !== null && existing !== undefined && existing !== "";
			if (contradictsACommittedValue) {
				reportUnresolvedEntry(
					"BelowMinSearchChars",
					tooShortDetail,
					`"${searchText}" is too short to look up. Please type at least ${getMinSearchChars(field)} characters or pick from the list.`
				);
				return;
			}
			skip("BelowMinSearchChars", tooShortDetail);
		};

		// Below the field's own minimum the entry cannot be a valid value, so there is nothing to
		// resolve and no reason to spend a request finding that out.
		// This also covers a DEBOUNCED fetch the backend rejected as too short: its stated minimum was learned,
		// so the gate sees it here, before the debounce-failure branch below could route it anywhere else.
		if (searchText.length < getMinSearchChars(field)) {
			reportTooShortEntry();
			return;
		}
		if (applyUnambiguousMatch(field, searchText)) return;

		// The options may simply not have arrived yet. Blur lands inside the 300ms search debounce or
		// the fetch's round trip in exactly the reported repro — paste a job number and click straight
		// out — and the arriving options do not retroactively commit anything. So run the lookup this
		// commit needs rather than discarding a value the user did supply.
		if (!field.mcpDataSource) {
			// A static-option field has no lookup to run, so its options ARE the final answer and
			// applyUnambiguousMatch above already failed against them — the same position as NoPendingLookup.
			//
			// Unconditional for single-select, like that exit. Unlike BelowMinSearchChars — where an empty
			// field is just a half-typed entry the "type at least N characters" hint already covers — this is
			// a COMPLETE entry matching nothing, so an empty field still deserves the explanation: otherwise
			// the text vanishes on blur and a required field leaves Run looking dead for no stated reason.
			if (field.type !== "multi_autocomplete") {
				reportUnresolvedEntry(
					"NoMcpDataSource",
					`SearchText=${searchText}`,
					`We could not match "${searchText}" to a single option. Please pick one from the list.`
				);
				return;
			}
			skip("NoMcpDataSource");
			return;
		}
		// No lookup pending or running means the options on hand are already the final answer for this
		// text — and applyUnambiguousMatch above just failed against them. So the entry did not resolve and
		// nothing more is coming: report it rather than only logging. Fetching again is still wrong (it
		// would flash the spinner for no new information), which is why this stays a distinct exit.
		//
		// Single-select ONLY, and the reason is VAutocomplete's `select()` rather than a guess. Its
		// single-select branch runs `search.value = add && !hasSelectionSlot.value ? item.title : ""`, so a
		// pick leaves the option's FULL TITLE in the query — which resolves through applyUnambiguousMatch
		// above, or through reportUnresolvedEntry's echo guard when two options share that title. A leftover
		// query that is neither therefore genuinely means the user typed something that did not resolve.
		//
		// `clearOnSelect` is NOT what makes that true: `select()` consults it only inside its `props.multiple`
		// branch, so the template's `:clear-on-select="field.type !== 'multi_autocomplete'"` is inert for
		// single-select and an earlier version of this comment leaned on it wrongly. What the binding does do
		// is keep the query alive for multi_autocomplete, where it sits beside the new chip — so blur would
		// report a failure right after a pick that worked. A menu pick CAN reach this exit; both halves are
		// handled, one by resolving and one by staying quiet.
		//
		// Nothing is lost by staying quiet there: reportUnresolvedEntry does not clear a multi-select value
		// anyway (chips are additive), so the only effect would have been a spurious error message.
		// Still synchronous at this point, so no supersede check is needed; nothing has been awaited.
		if (!searchDebounceTimers.value[field.key] && !loadingFields.value[field.key]) {
			// A lookup that FAILED also leaves both flags false, and its empty option list is not an
			// answer — it is the absence of one. Without this the backend being down read as "we looked
			// and your entry is not a real option", clearing a value the user had legitimately committed.
			// LookupFailed already says this properly for the commit's OWN fetch; a debounce-triggered
			// failure could never reach it.
			const debounceFailure = lastFetchFailure.value[field.key];
			// No length condition. It used to require an empty option list, which is precisely the state
			// this case cannot reach: the failed fetch calls setMcpOptions(key, []), and setMcpOptions
			// PRESERVES the committed option row whenever a value is present — so getFieldOptions returns
			// one row, the guard was bypassed, and a backend outage was reported to the user as "we could
			// not match your entry", after nulling the value they had legitimately committed. The guard
			// only worked in the case where nothing was at stake.
			// The generation match is what makes this trustworthy. A superseded fetch's failure can land
			// AFTER a newer one succeeded, so "a failure exists" alone would route a genuinely unresolved
			// entry to LookupFailed and say nothing to the user. handleAutocompleteSearch bumps the
			// generation on every keystroke, so only the latest attempt's failure can match.
			if (debounceFailure !== undefined && debounceFailure.generation === fieldRequestGenerations.value[field.key]) {
				if (debounceFailure.kind === "rejected-input") {
					reportRejectedEntry(`SearchText=${searchText}, Cause=${debounceFailure.message}, FromDebouncedFetch=true`);
					return;
				}
				reportLookupFailure(`SearchText=${searchText}, Cause=${debounceFailure.message}, FromDebouncedFetch=true`);
				return;
			}
			if (field.type === "multi_autocomplete") {
				skip("NoPendingLookup", `SearchText=${searchText}, MultiSelectLeftoverQuery=true`);
				return;
			}
			reportUnresolvedEntry(
				"NoPendingLookup",
				`SearchText=${searchText}`,
				`We could not match "${searchText}" to a single option. Please pick one from the list.`
			);
			return;
		}
		if (searchDebounceTimers.value[field.key]) {
			clearTimeout(searchDebounceTimers.value[field.key]);
			delete searchDebounceTimers.value[field.key];
		}
		const fetched = await fetchMcpFieldOptions(field, searchText);
		if (!fetched) {
			// Names the underlying cause when there is one. Without this the commit's only trace would be
			// "no rows", indistinguishable from a genuinely empty result — and on this path the fetch's own
			// console.error is suppressed as superseded, so nothing else would say the backend failed.
			const supersededBeforeFailure = commitWasSuperseded();
			if (supersededBeforeFailure) {
				// The lookup failed, but the user had already moved on — so the failure is not about anything
				// currently on screen. Reporting it would clear a value they just chose, purely because a
				// request they no longer care about happened to fail afterwards.
				skip(supersededBeforeFailure, `SearchText=${searchText}, AfterLookupFailure=true`);
				return;
			}
			const failure = lastFetchFailure.value[field.key];
			if (failure?.kind === "below-minimum") {
				reportTooShortEntry(`, Cause=${failure.message}`);
				return;
			}
			if (failure?.kind === "rejected-input") {
				reportRejectedEntry(`SearchText=${searchText}, Cause=${failure.message}`);
				return;
			}
			reportLookupFailure(`SearchText=${searchText}, Cause=${failure?.message ?? "unreported"}`);
			return;
		}

		// Match against what WE fetched, not the shared store. Vuetify emits `update:search("")` as
		// part of its own blur handling — after this handler's synchronous half — and that runs
		// handleAutocompleteSearch(""), which bumps the request generation and empties
		// mcpOptions[field.key]. So by the time this await resolves the store is guaranteed useless and
		// the fetch's own store write has already been discarded as superseded.
		//
		// Three separate ways the user can supersede this commit, and the empty search text that
		// Vuetify itself leaves behind must not be mistaken for any of them:
		//   1. they re-focused and typed something else (non-empty and different),
		//   2. they deliberately cleared the field or typed anew — caught by the revision counter, which
		//      Vuetify's own post-blur clear does not advance,
		//   3. they picked from the menu, changing the selection.
		// A dependency changing mid-lookup invalidates this whole result: the rows were resolved under the
		// OLD parent value. handleFieldChange already reset formValues/mcpOptions for this field, and none of
		// the three guards below can see that — the reset writes null, which for a paste-commit equals the
		// selection we captured, so SelectionChangedDuringLookup compares equal and passes. Without this the
		// setMcpOptions below would overwrite that reset with stale rows and commit into the wrong context.
		// ONE source of truth. These four checks used to be written out again here, byte for byte identical
		// to commitWasSuperseded() above — and a duplicated rule is worse than no rule, because it makes
		// every negative control on it vacuous: narrowing the guard in one place left this copy firing, so a
		// test could not tell a working guard from a broken one. A review flagged the test; the test was only
		// the symptom.
		const supersededAfterFetch = commitWasSuperseded();
		if (supersededAfterFetch) {
			skip(
				supersededAfterFetch,
				`Was=${searchText}, Now=${(autocompleteSearchText.value[field.key] || "").trim()}, Trigger=${trigger}`
			);
			return;
		}

		const match = findUnambiguousOption(field, searchText, fetched);
		if (!match) {
			reportUnresolvedEntry(
				"NoUnambiguousMatch",
				`SearchText=${searchText}, OptionCount=${fetched.length}`,
				`We could not match "${searchText}" to a single option. Please pick one from the list.`
			);
			return;
		}
		// The rows must also land in the shared store, not merely drive the commit: buildEnrichedValues
		// looks the committed value up in mcpOptions to attach displayLabel and mcpSource.record, and
		// dependent-field mappings read that record. This fetch lost the generation race to Vuetify's
		// blur-driven update:search(""), so its own store write was skipped — without this the value
		// would commit with no label and no record, and the run would get an incomplete payload.
		setMcpOptions(field.key, fetched);
		applyUnambiguousMatch(field, searchText, fetched);
	};

	const handleFieldChange = (field: FieldDefinition) => {
		if (field.type === "autocomplete" || field.type === "multi_autocomplete") {
			const value = formValues.value[field.key];
			const nowHasAValue = Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== "";
			// A menu pick lands here without going through applyUnambiguousMatch and resolves the entry just
			// as well, so the warning must not outlive it.
			if (nowHasAValue) {
				delete unresolvedEntries.value[field.key];
				// A committed value means a lookup accepted this entry, so a rejection recorded for an earlier one is
				// stale — including when the commit's own fetch resolved it but lost the generation race to Vuetify's
				// post-blur clear, and so never got to clear it itself (LSP-19462).
				delete fieldErrors.value[field.key];
			}
			if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
				// The field just became empty — a clear. That is a user action, and it must invalidate any
				// blur-commit still waiting on its lookup.
				noteAutocompleteUserInteraction(field.key);
				const searchText = autocompleteSearchText.value[field.key] || "";
				if (field.type === "multi_autocomplete" && searchText.length >= getMinSearchChars(field)) {
					if (loadingFields.value[field.key] || searchDebounceTimers.value[field.key]) return;
					handleAutocompleteSearch(field, searchText);
				} else {
					fieldRequestGenerations.value[field.key] = (fieldRequestGenerations.value[field.key] || 0) + 1;
					delete fieldErrors.value[field.key];
					delete unresolvedEntries.value[field.key];
					loadingFields.value[field.key] = false;
					mcpOptions.value[field.key] = [];
				}
			}
		}
		const dependentFields = props.schema.fields.filter((f) => f.mcpDataSource?.dependsOn?.includes(field.key));

		for (const depField of dependentFields) {
			fieldRequestGenerations.value[depField.key] = (fieldRequestGenerations.value[depField.key] || 0) + 1;
			autocompleteContextEpoch.value[depField.key] = (autocompleteContextEpoch.value[depField.key] || 0) + 1;
			delete fieldErrors.value[depField.key];
			delete unresolvedEntries.value[depField.key];
			loadingFields.value[depField.key] = false;
			if (searchDebounceTimers.value[depField.key]) {
				clearTimeout(searchDebounceTimers.value[depField.key]);
				delete searchDebounceTimers.value[depField.key];
			}
			formValues.value[depField.key] = depField.type === "multiselect" || depField.type === "multi_autocomplete" ? [] : null;
			mcpOptions.value[depField.key] = [];
			fetchMcpFieldOptions(depField);
		}
	};

	const fetchAllMcpOptions = async () => {
		const mcpFields = props.schema.fields.filter(
			(f) => f.mcpDataSource && f.type !== "autocomplete" && f.type !== "multi_autocomplete"
		);

		const independentFields = mcpFields.filter((f) => !f.mcpDataSource?.dependsOn?.length);
		await Promise.all(independentFields.map((f) => fetchMcpFieldOptions(f)));

		const dependentFields = mcpFields.filter((f) => f.mcpDataSource?.dependsOn?.length);
		for (const field of dependentFields) {
			if (!isFieldDisabled(field)) {
				await fetchMcpFieldOptions(field);
			}
		}
	};

	const buildEnrichedValues = (rawValues: Record<string, any>): Record<string, any> => {
		const enriched: Record<string, any> = {};
		for (const field of props.schema.fields) {
			const rawValue = rawValues[field.key];
			if (rawValue === undefined) continue;

			const base: Record<string, any> = {
				value: rawValue,
				fieldLabel: field.label,
				fieldType: field.type
			};

			if (field.mcpDataSource && rawValue != null && rawValue !== "") {
				const options = mcpOptions.value[field.key] || [];
				const selectedValues = Array.isArray(rawValue) ? rawValue : [rawValue];
				const selectedOptions = selectedValues
					.map((v) => options.find((o) => o.value === v))
					.filter((o): o is UserInputFieldOption => o != null);

				if (selectedOptions.length) {
					const selectedInputParams = selectedOptions
						.map((option) => (option as McpOptionWithProvenance)._mcpRequestParams)
						.filter((value): value is Record<string, unknown> => value != null);

					base.displayLabel = Array.isArray(rawValue) ? selectedOptions.map((o) => o.label) : selectedOptions[0].label;
					base.mcpSource = {
						toolName: field.mcpDataSource.toolName,
						platformCodeName: field.mcpDataSource.platformCodeName,
						...(selectedInputParams.length > 0
							? {
									inputParams: Array.isArray(rawValue) ? selectedInputParams : selectedInputParams[0]
								}
							: {}),
						record: Array.isArray(rawValue)
							? selectedOptions.map((o) => o.record).filter((r) => r != null)
							: selectedOptions[0].record
					};
				} else {
					base.mcpSource = {
						toolName: field.mcpDataSource.toolName,
						platformCodeName: field.mcpDataSource.platformCodeName
					};
				}
			}

			enriched[field.key] = base;
		}
		return enriched;
	};

	watch(
		formValues,
		(newValues) => {
			emit("update:modelValue", buildEnrichedValues(newValues));
		},
		{ deep: true }
	);

	watch(
		() => props.schema,
		(newSchema, oldSchema) => {
			// initializeFormValues() below wipes formValues and empties autocompleteSearchText, and NONE of the
			// commit's guards can see that: the epoch is not bumped, the emptied search text is
			// indistinguishable from Vuetify's own post-blur clear, the revision is not bumped, and the reset
			// writes null — which for a not-yet-committed field equals the captured selection. So a commit
			// resolving against the OLD schema's options would land in the NEW schema's values.
			//
			// initializeFormValues() invalidates on its own now, so this call exists only for the keys it
			// cannot know: fields that were in the OLD schema and are gone from the new one. An extra bump
			// for the overlapping keys is harmless — the epoch is only ever compared for equality.
			invalidateAutocompleteContexts((oldSchema?.fields ?? []).map((field) => field.key));
			initializeFormValues();
			fetchAllMcpOptions();
		},
		{ deep: true }
	);

	watch(
		() => props.modelValue,
		(newValue) => {
			if (!newValue) return;
			const rawModelValues: Record<string, any> = {};
			for (const field of props.schema.fields) {
				if (newValue[field.key] !== undefined) {
					rawModelValues[field.key] = extractRawValue(newValue[field.key]);
				}
			}
			if (JSON.stringify(rawModelValues) !== JSON.stringify(formValues.value)) {
				seedMcpOptionsFromModelValue();
				initializeFormValues();
			}
		},
		{ deep: true }
	);

	const autoPopulateFirstAutocomplete = async () => {
		if (!props.customer) return;
		const displayName = getCustomerDisplayName(props.customer);
		if (!displayName) return;

		const field = props.schema.fields.find((f) => !f.hidden && (f.type === "autocomplete" || f.type === "multi_autocomplete"));
		if (!field || !field.mcpDataSource) return;

		// Saved data from a previous run takes precedence
		if (props.modelValue && props.modelValue[field.key] !== undefined) return;

		// The customer's own name is not exempt from the field's threshold. This runs from `onMounted`,
		// so firing a doomed request here produced a red field error before the user had interacted with
		// the form at all — the ticket's symptom on a route that involves no typing. Seeding the text box
		// is still correct; only the request is withheld.
		if (displayName.length < getMinSearchChars(field)) {
			autocompleteSearchText.value[field.key] = displayName;
			return;
		}

		// Search for the customer name and select the first result
		autocompleteSearchText.value[field.key] = displayName;
		await fetchMcpFieldOptions(field, displayName);

		const options = mcpOptions.value[field.key] || [];
		if (options.length > 0) {
			formValues.value[field.key] = options[0].value;
		}
	};

	// Registered so that when the hosting dialog clears the barrier, this renderer's in-flight commits
	// are invalidated in the same breath. Closing a dialog that stays MOUNTED drops the barrier's
	// tracking but resets nothing here — no epoch bump, no initializeFormValues (the parent's data is
	// unchanged, so the watchers no-op) — which left a commit abandoned at the 5s drain still running
	// under a 20s request bound, with every post-await guard intact, free to land in the REOPENED form
	// with nothing tracking it. Bumping the context epoch is what those guards read to refuse.
	// Registered so the hosting dialog's proceed can refuse while this renderer has an entry it could
	// not check. A failed lookup keeps the committed value rather than destroying it, which is right —
	// but the value on screen is then not the value in the model, and without this the run went out on
	// the one the user replaced, with only a banner to show for it.
	//
	// A live predicate, not a flag the barrier stores: fixing the field empties unresolvedEntries and
	// the refusal lifts on its own, so a transient backend failure never requires reopening the dialog.
	// Deliberately NOT scoped to entries whose text is still on screen. I tried that to fix the
	// unescapable-block case below and a mutation showed it was both redundant and HARMFUL: Vuetify
	// empties the search text as part of its own blur handling, so scoping the probe to non-empty
	// text let that emit lift the block on a value the user had replaced — silently restoring the
	// stale submission this was chosen to prevent. The escape hatch belongs where the warning is
	// cleared, not where it is read.
	// A WARNING MUST NOT OUTLIVE ITS FIELD BEING ON SCREEN. Nothing pruned unresolvedEntries when a
	// `visibleWhen` condition hid a field — only mcpDataSource.dependsOn dependents are cleared, in
	// handleFieldChange — so an unresolvable entry on field B, followed by a change to field A that
	// hides B, blocked proceed forever: the probe below counts it, the alert names an off-screen
	// field, and the only recovery was closing and reopening the dialog, losing the form.
	//
	// Pruned here rather than by scoping the probe to visibleFields, which was the other option and is
	// NOT equivalent: buildEnrichedValues iterates props.schema.fields, so a hidden field's value is
	// still submitted. Scoping only the READ would leave the warning's subject in the payload while
	// silently lifting the block — the stale-submission bug this ticket exists to close. Dropping the
	// entry is the honest version: the field is off screen, so the protection no longer applies to it,
	// and that matches the pre-ticket behaviour for exactly this case rather than weakening the new
	// protection for visible ones.
	watch(
		() => visibleFields.value.map((field) => field.key).join("\u0000"),
		() => {
			const stillVisible = new Set(visibleFields.value.map((field) => field.key));
			for (const fieldKey of Object.keys(unresolvedEntries.value)) {
				if (stillVisible.has(fieldKey)) continue;
				console.warn(`[user-input] Dropping unresolved entry for a field that is no longer visible: FieldKey=${fieldKey}`);
				delete unresolvedEntries.value[fieldKey];
			}
		}
	);
	const stopUnresolvedProbe = commitBarrier.registerUnresolvedProbe(() => Object.keys(unresolvedEntries.value).length > 0);
	onUnmounted(stopUnresolvedProbe);

	const stopInvalidatingOnBarrierClear = commitBarrier.onClear(() => {
		invalidateAutocompleteContexts([...Object.keys(autocompleteContextEpoch.value), ...props.schema.fields.map((field) => field.key)]);
	});
	onUnmounted(() => {
		stopInvalidatingOnBarrierClear();
		// Going Back from this step UNMOUNTS this renderer while the dialog stays open. Nothing here
		// used to run: the commit stayed in the barrier, timed out at the 5s drain, and marked it stuck
		// — so the previous step's Continue was refused on account of a form no longer on screen.
		//
		// Invalidate FIRST, then withdraw. Invalidating is what makes withdrawing safe: the commit can
		// no longer write, so its outcome stops mattering to anyone. Withdrawing without it would just
		// re-create the bug clear() had — untracked work still able to land.
		invalidateAutocompleteContexts([...Object.keys(autocompleteContextEpoch.value), ...props.schema.fields.map((field) => field.key)]);
		for (const commit of ownCommits) {
			commitBarrier.untrack(commit);
		}
		ownCommits.clear();
	});

	onMounted(async () => {
		seedMcpOptionsFromModelValue();
		initializeFormValues();
		await autoPopulateFirstAutocomplete();
		fetchAllMcpOptions();
	});

	onBeforeUnmount(() => {
		Object.values(searchDebounceTimers.value).forEach((timer) => clearTimeout(timer));
	});
</script>

<style scoped>
	.user-input-form-renderer {
		width: 100%;
	}

	.form-fields {
		display: flex;
		flex-wrap: wrap;
		gap: 16px;
	}

	.form-fields.layout-vertical {
		flex-direction: column;
	}

	.form-fields.layout-horizontal {
		flex-direction: row;
	}

	.field-wrapper {
		min-width: 0;
	}

	.field-wrapper.width-full {
		width: 100%;
		flex: 0 0 100%;
	}

	.field-wrapper.width-half {
		width: calc(50% - 8px);
		flex: 0 0 calc(50% - 8px);
	}

	.field-wrapper.width-third {
		width: calc(33.333% - 11px);
		flex: 0 0 calc(33.333% - 11px);
	}

	@media (max-width: 600px) {
		.field-wrapper.width-half,
		.field-wrapper.width-third {
			width: 100%;
			flex: 0 0 100%;
		}
	}
</style>
